<script setup lang="ts">
import { inject } from 'vue'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    danger?: boolean
    divided?: boolean
  }>(),
  {
    disabled: false,
    danger: false,
    divided: false
  }
)

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const closeDropdown = inject<(() => void) | null>('rrDropdownClose', null)

function handleClick(event: MouseEvent) {
  if (props.disabled) return
  emit('click', event)
  if (closeDropdown) {
    closeDropdown()
  }
}
</script>

<template>
  <div>
    <div v-if="divided" class="rr-menu-item__divider" />
    <div
      class="rr-menu-item"
      role="menuitem"
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled"
      @keydown.enter.prevent="($event.currentTarget as HTMLElement).click()"
      @keydown.space.prevent="($event.currentTarget as HTMLElement).click()"
      :class="{
        'is-disabled': disabled,
        'is-danger': danger
      }"
      @click="handleClick"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.rr-menu-item__divider {
  height: 1px;
  background: var(--rr-color-border, #e1e5ea);
  margin: 4px -4px;
}

.rr-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: var(--rr-font-size-base, 13px);
  color: var(--rr-color-text, #2b3038);
  border-radius: var(--rr-radius-xs, 4px);
  cursor: pointer;
  user-select: none;
  transition: background 120ms ease, color 120ms ease;
  white-space: nowrap;
}

.rr-menu-item:hover:not(.is-disabled) {
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-text-strong, #171a1f);
}

.rr-menu-item.is-danger {
  color: var(--rr-color-danger, #be3038);
}

.rr-menu-item.is-danger:hover:not(.is-disabled) {
  background: var(--rr-color-danger-soft, #fff0f1);
  color: var(--rr-color-danger, #be3038);
}

.rr-menu-item.is-disabled {
  color: var(--rr-color-text-subtle, #8a94a2);
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
